import { useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { MapPin, Calendar, User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ImportStatusBadge } from './import-status-badge';
import type { TrackingEvent, ImportOrderLeg } from '../types/import-tracking.types';

/**
 * Vertical timeline of tracking events, styled like a courier tracking page.
 * Most recent event at the top.
 * When legs are provided, events are grouped by legId with a header per tramo.
 */

interface ShipmentTimelineProps {
  events: TrackingEvent[];
  isLoading?: boolean;
  className?: string;
  legs?: ImportOrderLeg[];
}

const TRANSPORT_MODE_LABEL: Record<string, string> = {
  SEA: 'Marítimo',
  AIR: 'Aéreo',
  LAND: 'Terrestre',
  RAIL: 'Ferroviario',
  MULTIMODAL: 'Multimodal',
};

export function ShipmentTimeline({ events, isLoading, className, legs }: ShipmentTimelineProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, TrackingEvent[]>();
    for (const ev of events) {
      const k = ev.legId ?? '__orphan__';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(ev);
    }
    return map;
  }, [events]);

  const legById = useMemo(() => {
    const m = new Map<string, ImportOrderLeg>();
    for (const l of legs ?? []) m.set(l.id, l);
    return m;
  }, [legs]);

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={cn('text-center py-8', className)}>
        <p className="text-muted-foreground">No hay eventos de seguimiento registrados</p>
      </div>
    );
  }

  // If no legs prop provided or all events are orphans, render the flat timeline (original behavior)
  const hasGroups = legs && legs.length > 0;

  const renderEventList = (evs: TrackingEvent[], globalFirstId: string) => (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-[7px] top-3 bottom-3 w-0.5 bg-border" />
      <div className="space-y-6">
        {evs.map((event) => {
          const isFirst = event.id === globalFirstId;
          const eventDate = new Date(event.eventDate);

          return (
            <div key={event.id} className="relative flex gap-4 pl-0">
              {/* Dot */}
              <div
                className={cn(
                  'relative z-10 w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5',
                  isFirst ? 'bg-primary border-primary' : 'bg-background border-muted-foreground/40'
                )}
              />

              {/* Content */}
              <div className="flex-1 pb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn('font-medium text-sm', isFirst && 'text-primary')}>
                    {event.title}
                  </span>
                  <ImportStatusBadge status={event.status} />
                </div>

                {event.description && (
                  <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                )}

                <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {eventDate.toLocaleDateString('es-BO', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}{' '}
                    {eventDate.toLocaleTimeString('es-BO', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {event.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {event.location}
                    </span>
                  )}
                  {event.createdBy && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {event.createdBy.firstName} {event.createdBy.lastName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Flat render (no legs)
  if (!hasGroups) {
    return (
      <div className={cn('relative', className)}>{renderEventList(events, events[0]?.id)}</div>
    );
  }

  // Grouped render: one section per leg (ordered), then orphans at the end
  const globalFirstId = events[0]?.id;

  // Build ordered group keys: legs in legOrder, then orphans
  const orderedKeys: string[] = [
    ...(legs ?? [])
      .slice()
      .sort((a, b) => a.legOrder - b.legOrder)
      .map((l) => l.id)
      .filter((id) => grouped.has(id)),
    ...(grouped.has('__orphan__') ? ['__orphan__'] : []),
  ];

  return (
    <div className={cn('space-y-6', className)}>
      {orderedKeys.map((key) => {
        const groupEvents = grouped.get(key)!;
        const leg = key !== '__orphan__' ? legById.get(key) : undefined;

        const headerLabel =
          key === '__orphan__'
            ? 'Sin tramo asignado'
            : leg
              ? `Tramo ${leg.legOrder}: ${leg.origin} → ${leg.destination} (${TRANSPORT_MODE_LABEL[leg.transportMode] ?? leg.transportMode})`
              : 'Tramo';

        return (
          <div key={key}>
            <div className="mb-3 flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2">
                {headerLabel}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            {renderEventList(groupEvents, globalFirstId)}
          </div>
        );
      })}
    </div>
  );
}
